import { useMemo, useState } from 'react';
import { SegmentedControl } from '@ai-tutor/ui';
import { Check, ChevronDown, ChevronRight, FileSearch, X } from 'lucide-react';
import type {
  QuestionAttempt,
  SessionOutcome,
} from '@/lib/practice/session-types';
import {
  gradeLabel,
  gradeTone,
} from '@/components/features/mindspace/practice/utils';

interface OutcomeAuditListProps {
  outcomes: SessionOutcome[];
  /** Defaults to false → user expands rows on demand. Pass true to
   *  preopen every row (used on the post-session Results screen). */
  defaultExpanded?: boolean;
}

/**
 * Per-concept audit list - expandable rows showing the actual Q&A
 * captured during the session. Shared between `SessionResults` and
 * `SessionDetailView` so the post-run page and the historical detail
 * view show the same information.
 *
 * Filter: `All` / `Weak only`, a segmented control. Defaults to All. Weak ≡
 * grade ≤ 2.
 *
 * Legacy outcomes without an `attempts` array show a faint "history
 * not captured" notice instead of crashing.
 */
export function OutcomeAuditList({
  outcomes,
  defaultExpanded = false,
}: OutcomeAuditListProps) {
  const [filter, setFilter] = useState<'all' | 'weak'>('all');
  const weakCount = useMemo(
    () => outcomes.filter(o => o.grade <= 2).length,
    [outcomes]
  );
  const visible = useMemo(
    () => (filter === 'weak' ? outcomes.filter(o => o.grade <= 2) : outcomes),
    [outcomes, filter]
  );

  if (outcomes.length === 0) return null;

  return (
    <section>
      <header className='mb-2 flex items-center justify-between'>
        <h3 className='text-sm font-semibold text-foreground'>
          Review your answers
        </h3>
        {weakCount > 0 && (
          // The app's single-choice control: it was a pair of pressed-state
          // buttons whose "on" was a grey fill a shade off the card.
          <SegmentedControl
            label='Show answers'
            options={[
              { value: 'all', label: `All (${outcomes.length})` },
              { value: 'weak', label: `Weak only (${weakCount})` },
            ]}
            value={filter}
            onChange={setFilter}
            size='sm'
            segmentClassName='tap-target'
          />
        )}
      </header>

      <ul className='divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card'>
        {visible.map(outcome => (
          <AuditRow
            key={outcome.conceptId}
            outcome={outcome}
            defaultExpanded={defaultExpanded}
          />
        ))}
      </ul>
    </section>
  );
}

function AuditRow({
  outcome,
  defaultExpanded,
}: {
  outcome: SessionOutcome;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasAttempts = (outcome.attempts?.length ?? 0) > 0;
  const tone = gradeTone(outcome.grade as 1 | 2 | 3 | 4);
  const dotCls = {
    emerald: 'bg-emerald-500',
    indigo: 'bg-brand-500 dark:bg-primary',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
  }[tone];

  return (
    <li>
      <button
        type='button'
        onClick={() => setExpanded(e => !e)}
        className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted dark:hover:bg-white/[0.04]'
      >
        <div className='flex min-w-0 items-center gap-2'>
          {expanded ? (
            <ChevronDown className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
          ) : (
            <ChevronRight className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
          )}
          <span className='truncate text-sm font-medium text-foreground'>
            {outcome.conceptName}
          </span>
        </div>
        <div className='flex flex-shrink-0 items-center gap-3 text-xs'>
          <span className='inline-flex items-center gap-1.5 font-medium text-foreground'>
            <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
            {gradeLabel(outcome.grade)}
          </span>
          <span className='tabular-nums text-muted-foreground'>
            {Math.round(outcome.retention * 100)}%
          </span>
        </div>
      </button>
      {expanded && (
        <div className='border-t border-border bg-muted/40 px-5 py-4 dark:bg-white/[0.02]'>
          {hasAttempts ? (
            <div className='space-y-4'>
              {outcome.attempts!.map((attempt, i) => (
                <AttemptCard
                  key={`${outcome.conceptId}-${i}`}
                  attempt={attempt}
                  index={i}
                  total={outcome.attempts!.length}
                  grade={outcome.grade as 1 | 2 | 3 | 4}
                />
              ))}
            </div>
          ) : (
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <FileSearch className='h-3.5 w-3.5 text-muted-foreground' />
              Question-by-question history wasn't captured for this session.
            </div>
          )}
        </div>
      )}
    </li>
  );
}

const GRADE_ANSWER_CLS: Record<string, string> = {
  emerald:
    'border-emerald-200 bg-emerald-50/40 dark:border-success/30 dark:bg-success/20',
  indigo:
    'border-brand-200 bg-brand-50/40 dark:border-primary/30 dark:bg-primary/20',
  amber:
    'border-amber-200 bg-amber-50/40 dark:border-warning/30 dark:bg-warning/20',
  rose: 'border-rose-200 bg-rose-50/40 dark:border-destructive/30 dark:bg-destructive/20',
};

const sameAnswer = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * One MCQ option in the audit. The pick and the correct option used to be
 * told apart by tint alone (WCAG 1.4.1); each marked row now carries a
 * visible "Your answer" / "Correct answer" tag with an icon — the same words
 * the short-answer rendering uses — and names itself for assistive tech
 * ("Keratin (your answer)"), since a listitem's content is not its name.
 * Tag text is the -700 step on these tints, as the short-answer labels are.
 */
const OPTION_TAG =
  'inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold uppercase tracking-wide';

function optionRowCls(isCorrect: boolean, isPicked: boolean): string {
  if (isCorrect) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-success/30 dark:bg-success/20 dark:text-emerald-100';
  }
  if (isPicked) {
    return 'border-rose-200 bg-rose-50 text-rose-900 dark:border-destructive/30 dark:bg-destructive/20 dark:text-rose-100';
  }
  return 'border-border bg-card text-muted-foreground';
}

/** "Your answer" — a tick when the pick was right, a cross when wrong. */
function PickedTag({ isCorrect }: { isCorrect: boolean }) {
  const Icon = isCorrect ? Check : X;
  return (
    <span
      className={`${OPTION_TAG} ${
        isCorrect
          ? 'text-emerald-700 dark:text-success'
          : 'text-rose-700 dark:text-destructive-emphasis'
      }`}
    >
      <Icon className='h-3 w-3' aria-hidden />
      Your answer
    </span>
  );
}

function McqOptionRow({
  option,
  isCorrect,
  isPicked,
}: {
  option: string;
  isCorrect: boolean;
  isPicked: boolean;
}) {
  const marks = [
    isPicked && 'your answer',
    isCorrect && 'correct answer',
  ].filter(Boolean);
  return (
    <li
      aria-label={marks.length ? `${option} (${marks.join(', ')})` : option}
      className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${optionRowCls(isCorrect, isPicked)}`}
    >
      <span className='flex-1'>{option}</span>
      {isPicked && <PickedTag isCorrect={isCorrect} />}
      {isCorrect && (
        <span className={`${OPTION_TAG} text-emerald-700 dark:text-success`}>
          {/* The tick already sits on "Your answer" for a right pick. */}
          {!isPicked && <Check className='h-3 w-3' aria-hidden />}
          Correct answer
        </span>
      )}
    </li>
  );
}

function AttemptCard({
  attempt,
  index,
  total,
  grade,
}: {
  attempt: QuestionAttempt;
  index: number;
  total: number;
  grade?: 1 | 2 | 3 | 4;
}) {
  const isMcq = Array.isArray(attempt.options) && attempt.options.length > 0;
  // Short-answer = the quiz path emitted a `pickedAnswer` (the typed
  // response) but no `options`. The audit renders user-vs-correct as
  // two side-by-side blocks. Flashcard attempts have no `pickedAnswer`
  // and fall through to the "just the correct answer" branch.
  const isShortAnswer = !isMcq && attempt.pickedAnswer != null;
  return (
    <div className='rounded-lg border border-border bg-card p-4 text-sm'>
      <div className='mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        <span>Question{total > 1 ? ` ${index + 1} of ${total}` : ''}</span>
        {(isMcq || isShortAnswer) && attempt.wasCorrect !== undefined && (
          <span
            className={`inline-flex items-center rounded-sm px-1.5 py-0.5 ${
              attempt.wasCorrect
                ? 'bg-emerald-100 text-emerald-700 dark:bg-success/20 dark:text-success'
                : 'bg-rose-100 text-rose-700 dark:bg-destructive/20 dark:text-destructive-emphasis'
            }`}
          >
            {attempt.wasCorrect ? 'Correct' : 'Incorrect'}
          </span>
        )}
      </div>

      <p className='font-medium text-foreground'>{attempt.question}</p>

      {isMcq ? (
        <ul className='mt-3 space-y-1.5' aria-label='Answer options'>
          {attempt.options!.map((option, i) => (
            <McqOptionRow
              key={`${i}-${option}`}
              option={option}
              isCorrect={sameAnswer(option, attempt.correctAnswer)}
              isPicked={
                attempt.pickedAnswer != null &&
                sameAnswer(option, attempt.pickedAnswer)
              }
            />
          ))}
        </ul>
      ) : isShortAnswer ? (
        <div className='mt-3 space-y-2'>
          <div
            className={`rounded-md border-l-2 px-3 py-2 text-sm ${
              attempt.wasCorrect
                ? 'border-emerald-200 bg-emerald-50/40 text-foreground dark:border-success/30 dark:bg-success/20'
                : 'border-rose-200 bg-rose-50/40 text-foreground dark:border-destructive/30 dark:bg-destructive/20'
            }`}
          >
            {/* -700, not -600: at 10px on these tints the -600 steps measured
                3.53:1 (emerald) and 4.31:1 (rose), below AA — surfaced by
                `FiltersTheOutcomeAudit/ShowsEachKindOfAttempt`. */}
            <div
              className={`mb-0.5 text-xs font-semibold uppercase tracking-wide ${
                attempt.wasCorrect
                  ? 'text-emerald-700 dark:text-success'
                  : 'text-rose-700 dark:text-destructive-emphasis'
              }`}
            >
              Your answer
            </div>
            {attempt.pickedAnswer}
          </div>
          {!attempt.wasCorrect && (
            <div className='rounded-md border-l-2 border-emerald-200 bg-emerald-50/40 px-3 py-2 text-sm text-foreground dark:border-success/30 dark:bg-success/20'>
              <div className='mb-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-success'>
                Correct answer
              </div>
              {attempt.correctAnswer}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`mt-3 rounded-md border-l-2 px-3 py-2 text-sm text-foreground ${GRADE_ANSWER_CLS[gradeTone(grade ?? 4)]}`}
        >
          {attempt.correctAnswer}
        </div>
      )}

      {attempt.explanation && (
        <p className='mt-3 text-xs leading-relaxed text-muted-foreground'>
          {attempt.explanation}
        </p>
      )}
    </div>
  );
}
